import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '../../api/users.js';
import { rolesApi } from '../../api/roles.js';
import { areasApi } from '../../api/areas.js';

const AREA_BADGE_STYLE = {
  display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600,
  padding: '3px 9px', borderRadius: 20, background: '#eff6ff', color: '#2563eb',
  border: '1px solid #dbeafe', cursor: 'pointer', whiteSpace: 'nowrap',
};

// Small pill for one area — clicking it deep-links to the Areas page,
// pre-filled with this area's name in that page's own search box.
function AreaBadge({ area, onNavigate }) {
  return (
    <span
      style={AREA_BADGE_STYLE}
      onClick={(e) => { e.stopPropagation(); onNavigate(area.name); }}
      title={`Go to ${area.name} in Areas`}
    >
      {area.name}
    </span>
  );
}

// Shows only the first few areas inline (so the table row doesn't blow up
// with 100+ comma-separated names) plus a "+N more" button that opens a
// modal listing every area as the same clickable badge UI.
const AREA_PREVIEW_COUNT = 6;
function AreaBadgeList({ areas, onNavigate }) {
  const [showAll, setShowAll] = useState(false);
  if (!areas || areas.length === 0) {
    return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>;
  }
  const preview = areas.slice(0, AREA_PREVIEW_COUNT);
  const remaining = areas.length - preview.length;
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, maxWidth: 420 }}>
        {preview.map((a) => <AreaBadge key={a.id} area={a} onNavigate={onNavigate} />)}
        {remaining > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
            style={{ ...AREA_BADGE_STYLE, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', fontWeight: 700 }}
          >
            +{remaining} more
          </button>
        )}
      </div>
      {showAll && (
        <div className="modal-overlay" onClick={() => setShowAll(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <span style={{ fontWeight: 700 }}>All Areas ({areas.length})</span>
              <button className="close-btn" onClick={() => setShowAll(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {areas.map((a) => <AreaBadge key={a.id} area={a} onNavigate={onNavigate} />)}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function mapUser(u) {
  const rawRole = u.Role?.name || u.role?.name || u.role || 'agent';
  // Normalize backend roles to consistent frontend use
  let role = rawRole;
  if (rawRole === 'sales_teamlead') {
    role = 'teamleader';
  } else if (rawRole === 'sales_rep') {
    role = 'salesman';
  }
  return {
    id: u.id,
    name: u.name || '',
    email: u.email || '',
    phone: u.phone || '',
    role,
    status: u.is_active === false ? 'Inactive' : 'Active',
    areas: u.areas || [],
    _raw: u,
  };
}

function displayRole(role) {
  if (!role) return '';
  const map = { admin: 'Admin', salesman: 'Salesman', teamleader: 'Team Leader', sales_teamlead: 'Team Leader', agent: 'Agent' };
  if (map[role]) return map[role];
  return role
    .split(/_|-/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function SalesmanModal({ salesman, onClose, onSave, roles = [], areas = [] }) {
  const [form, setForm] = useState(
    salesman
      ? { name: salesman.name, email: salesman.email, phone: salesman.phone, role: salesman.role, status: salesman.status, password: '', area_ids: salesman._raw?.areas?.map(a => a.id) || [] }
      : { name: '', email: '', phone: '', role: roles[0]?.name || '', status: 'Active', password: '', area_ids: [] }
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!salesman && roles.length > 0 && !form.role) {
      set('role', roles[0].name);
    }
  }, [roles]);

  const handleSave = async () => {
    if (!form.name || !form.email) { setErr('Name and email are required.'); return; }
    if (!salesman && !form.password) { setErr('Password is required for new users.'); return; }
    setSaving(true);
    setErr('');
    try {
      await onSave(form);
    } catch (e) {
      setErr(e.message || 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{salesman ? 'Edit Member' : 'Add Team Member'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
              {err}
            </div>
          )}
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Name" />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email" />
            </div>
            <div className="form-group">
              <label className="form-label">{salesman ? 'Password (leave blank to keep current)' : 'Password *'}</label>
              <input className="form-control" type="text" value={form.password} onChange={e => set('password', e.target.value)} placeholder={salesman ? "New password" : "Min 6 characters"} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Phone number" />
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="form-control" value={form.role} onChange={e => set('role', e.target.value)}>
                {roles.map(r => (
                  <option key={r.id} value={r.name}>
                    {displayRole(r.name)}
                  </option>
                ))}
              </select>
            </div>
            {['salesman', 'teamleader', 'sales_teamlead'].includes(form.role) && (
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Assigned Target Areas</label>
                {areas.length === 0 ? (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '6px 0' }}>No target areas defined. Go to Areas page to add.</div>
                ) : (
                  <div style={{
                    maxHeight: '140px',
                    overflowY: 'auto',
                    border: '1.5px solid var(--border)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    background: '#f8fafc',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: '10px'
                  }}>
                    {areas.map(a => {
                      const isChecked = form.area_ids.includes(a.id);
                      return (
                        <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-main)' }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            style={{ width: '15px', height: '15px', accentColor: 'var(--primary)' }}
                            onChange={() => {
                              const nextIds = isChecked
                                ? form.area_ids.filter(id => id !== a.id)
                                : [...form.area_ids, a.id];
                              set('area_ids', nextIds);
                            }}
                          />
                          <span>{a.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {salesman && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StaffsPage() {
  const navigate = useNavigate();
  const goToArea = (areaName) => navigate('/areas', { state: { search: areaName } });
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [roleFilter, setRoleFilter] = useState('All');
  const [roles, setRoles] = useState([]);
  const [areas, setAreas] = useState([]);

  // Server-side pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(20);

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const backendRole = roleFilter === 'teamleader' ? 'sales_teamlead' : (roleFilter === 'salesman' ? 'sales_rep' : roleFilter);
      const res = await usersApi.getAll({
        page,
        limit: limit === 100000 ? 100000 : limit,
        search: debouncedSearch || undefined,
        role: backendRole !== 'All' ? backendRole : undefined
      });
      const list = res.data ?? [];
      setTeam(Array.isArray(list) ? list.map(mapUser) : []);
      setTotalPages(res.totalPages || 1);
      setTotalItems(res.count || 0);
    } catch (err) {
      console.error('Failed to load team:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await rolesApi.getAll();
      const rawRoles = res.data ?? [];
      const normalizedRoles = rawRoles.map(r => {
        let name = r.name;
        if (name === 'sales_teamlead') name = 'teamleader';
        else if (name === 'sales_rep') name = 'salesman';
        return { ...r, name };
      });
      setRoles(normalizedRoles);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const fetchAreas = async () => {
    try {
      const res = await areasApi.getAll();
      setAreas(res.data ?? []);
    } catch (err) {
      console.error('Failed to load areas:', err);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, roleFilter, limit]);

  // Load team when page or filters change
  useEffect(() => {
    fetchTeam();
  }, [page, debouncedSearch, roleFilter, limit]);

  // Load roles and areas on mount
  useEffect(() => {
    fetchRoles();
    fetchAreas();
  }, []);

  const paginated = team;

  const handleSave = async (form) => {
    const isSalesOrLeader = ['salesman', 'teamleader', 'sales_teamlead'].includes(form.role);
    const assignedAreaIds = isSalesOrLeader ? (form.area_ids || []).map(id => parseInt(id, 10)) : [];

    if (editMember) {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        role: form.role === 'teamleader' ? 'sales_teamlead' : (form.role === 'salesman' ? 'sales_rep' : form.role),
        is_active: form.status === 'Active' ? true : false,
        area_ids: assignedAreaIds,
        ...(form.password ? { password: form.password } : {}),
      };
      await usersApi.update(editMember.id, payload);
    } else {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role === 'teamleader' ? 'sales_teamlead' : (form.role === 'salesman' ? 'sales_rep' : form.role),
        phone: form.phone || undefined,
        area_ids: assignedAreaIds,
      };
      await usersApi.create(payload);
    }
    setShowModal(false);
    setEditMember(null);
    fetchTeam();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await usersApi.remove(id);
      fetchTeam();
    } catch (err) {
      alert(err.message || 'Delete failed.');
    }
  };

  return (
    <div className="page-body">
      {showModal && (
        <SalesmanModal
          salesman={editMember}
          onClose={() => { setShowModal(false); setEditMember(null); }}
          onSave={handleSave}
          roles={roles}
          areas={areas}
        />
      )}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Staffs Management</span>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditMember(null); setShowModal(true); }}>+ Add Staff</button>
        </div>
        <div className="card-body">
          <div className="filters-row">
            <div className="search-bar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input placeholder="Search staffs…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="form-control" style={{ width: 'auto', marginLeft: '12px' }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
              <option value="All">All Roles</option>
              {roles.map(r => (
                <option key={r.id} value={r.name}>
                  {displayRole(r.name)}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading team…</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>ID</th><th>Name</th><th>Email</th><th>Mobile</th><th>Role</th><th>Area</th><th>TL</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr><td colSpan="8" style={{ textAlign: 'center', padding: '28px', color: 'var(--text-muted)' }}>No team members found</td></tr>
                  ) : paginated.map(m => (
                    <tr key={m.id}>
                      <td>#{m.id}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="av" style={{ width: 28, height: 28, fontSize: 11 }}>{m.name.charAt(0)}</div>
                          <span style={{ fontWeight: 600 }}>{m.name}</span>
                        </div>
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{m.email}</td>
                      <td style={{ fontSize: 12 }}>{m.phone || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td>
                        <span className={`badge badge-${m.role === 'admin' ? 'danger' : m.role === 'teamleader' ? 'primary' : 'gray'}`}>
                          {displayRole(m.role)}
                        </span>
                      </td>
                      <td>
                        <AreaBadgeList areas={m.areas} onNavigate={goToArea} />
                      </td>
                      <td>
                        {m._raw?.TeamLead ? (
                          <span style={{ fontSize: 12 }}>{m._raw.TeamLead.name} (ID: {m._raw.TeamLead.id})</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-${m.status === 'Active' ? 'success' : 'danger'}`}>{m.status}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-outline btn-xs" onClick={() => { setEditMember(m); setShowModal(true); }}>Edit</button>
                          <button className="btn btn-xs" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: 'none' }}
                            onClick={() => handleDelete(m.id)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && totalItems > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Show:</span>
                <select
                  className="form-control"
                  style={{ width: 'auto', padding: '4px 8px', fontSize: 13, cursor: 'pointer', height: 'auto' }}
                  value={limit === 100000 ? 'all' : limit}
                  onChange={e => { const v = e.target.value; setLimit(v === 'all' ? 100000 : parseInt(v, 10)); setPage(1); }}
                >
                  <option value={20}>20</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                  <option value="all">All</option>
                </select>
              </div>
              <button
                className="btn btn-outline btn-xs"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(p - 1, 1))}
                style={{ padding: '6px 14px', borderRadius: 6, cursor: page === 1 ? 'not-allowed' : 'pointer' }}
              >
                Previous
              </button>
              <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
                Page {page} of {totalPages} <span style={{ color: '#94a3b8', margin: '0 4px' }}>·</span> {totalItems} entries
              </span>
              <button
                className="btn btn-outline btn-xs"
                disabled={page === totalPages}
                onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                style={{ padding: '6px 14px', borderRadius: 6, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
